import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, Menu, X, ShoppingCart } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

interface NavbarProps {
  scrolled: boolean;
}

const navLinks = [
  { key: 'nav.home', href: '#home' },
  { key: 'nav.about', href: '#about' },
  { key: 'nav.tour', href: '#coffeetour' },
  { key: 'nav.contact', href: '#contact' },
  { key: 'nav.buy', href: '#buy' },
];

export default function Navbar({ scrolled }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { items, toggleCart } = useCartStore();
  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const { t } = useTranslation();

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#home');
            }}
            className="flex items-center gap-2 group"
          >
            <Coffee className={`w-7 h-7 group-hover:rotate-12 transition-transform ${scrolled ? 'text-coffee-400' : 'text-coffee-300'}`} />
            <span className={`font-display text-lg md:text-xl leading-none transition-colors ${scrolled ? 'text-coffee-900' : 'text-white'}`}>
              Café Los Sauces
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className={`relative text-sm font-medium transition-colors group ${
                  scrolled
                    ? 'text-coffee-800 hover:text-coffee-400'
                    : 'text-white/90 hover:text-coffee-300'
                }`}
              >
                {t(link.key)}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 transition-all group-hover:w-full rounded-full ${scrolled ? 'bg-coffee-400' : 'bg-coffee-300'}`} />
              </a>
            ))}
            <div className={scrolled ? '' : '[&>div]:bg-white/20 [&>div]:backdrop-blur-sm [&_button]:text-white [&_button:not(.bg-coffee-400)]:hover:text-coffee-300'}>
              <LanguageSwitcher scrolled={scrolled} />
            </div>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleCart}
              className={`relative p-2 rounded-full transition-colors ${scrolled ? 'hover:bg-coffee-100' : 'hover:bg-white/20'}`}
              aria-label="Abrir carrito"
            >
              <ShoppingCart className={`w-5 h-5 ${scrolled ? 'text-coffee-900' : 'text-white'}`} />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[1.25rem] h-5 px-1 flex items-center justify-center rounded-full bg-terracotta-400 text-white text-[10px] font-bold">
                  {totalItems}
                </span>
              )}
            </button>

            <button
              className={`md:hidden p-2 rounded-full transition-colors ${scrolled ? 'hover:bg-coffee-100' : 'hover:bg-white/20'}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <X className={`w-5 h-5 ${scrolled ? 'text-coffee-900' : 'text-white'}`} />
              ) : (
                <Menu className={`w-5 h-5 ${scrolled ? 'text-coffee-900' : 'text-white'}`} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.nav
              className="fixed top-16 right-0 w-72 max-w-full h-[calc(100vh-4rem)] bg-coffee-900/95 backdrop-blur-lg shadow-xl z-50 md:hidden flex flex-col gap-2 p-6"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.key}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }}
                  className="text-base font-medium text-white/90 py-3 border-b border-white/10 hover:text-coffee-300 transition-colors"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  {t(link.key)}
                </motion.a>
              ))}
              <div className="mt-4">
                <LanguageSwitcher scrolled={scrolled} />
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
